import React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../../../../components';
import { Actions, Backdrop, CardBox, DialogTitle, Message, Note } from './ConfirmDialog.styles';

export type ConfirmDialogProps = {
  /** Mount + show the dialog. Returns null when false (nothing portalled). */
  open: boolean;
  /** Title + message + optional note are already-translated strings (pass via `t()`
   *  at the call site so the copy is extracted), rendered through <Text>. */
  title: string;
  message: string;
  /** Optional secondary note, set off in its own inset box. */
  note?: string;
  /** Primary (confirm) button label, already translated. */
  confirmLabel: string;
  /** Dismiss button label, already translated. */
  cancelLabel: string;
  /** While true both buttons are disabled (the confirm action is in flight). */
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * A small, reusable confirmation dialog matching the acquisition ad-budgets /
 * top-up confirm: a centred card over a dimmed backdrop. Dismisses on Escape or a
 * click on the dim (never while `loading`). The caller owns the open state and the
 * confirm/cancel handlers.
 */
export const ConfirmDialog = ({
  open,
  title,
  message,
  note,
  confirmLabel,
  cancelLabel,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): React.ReactElement | null => {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !loading) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, loading, onCancel]);

  if (!open) return null;

  // Only a press that starts AND ends on the dim itself (not bubbled from the card)
  // dismisses, so a drag that ends on the backdrop doesn't close it.
  const onBackdropMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget && !loading) onCancel();
  };

  return createPortal(
    <Backdrop onMouseDown={onBackdropMouseDown} role="presentation">
      <CardBox role="dialog" aria-modal="true" aria-label={title}>
        <DialogTitle text={title} tag="span" />
        <Message text={message} tag="paragraph" />
        {note ? <Note text={note} tag="paragraph" /> : null}
        <Actions>
          <Button label={cancelLabel} variant="outline" onClick={onCancel} disabled={loading} />
          <Button label={confirmLabel} variant="primary" onClick={onConfirm} disabled={loading} />
        </Actions>
      </CardBox>
    </Backdrop>,
    document.body,
  );
};

export default ConfirmDialog;
